import React, { useState, useCallback, useMemo } from "react";
import {
    Card,
    Button,
    Input,
    Select,
    Table,
    Space,
    Badge,
    Tag,
    Tooltip,
    Divider,
    Typography,
    Spin,
} from "antd";
import {
    ReloadOutlined,
    InfoCircleOutlined,
    SwapOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { useLoadList, useQuickSearch } from "../../../hooks";
import { useTableSelection } from "../../../hooks/useTableSelection";
import { mailboxService } from "../../../api/enhancedApi";
import { formatDateTime } from "../../../utils/dateUtils";
import { useAuth } from "../../../hooks/useAuth";
import CollapsibleFilterBar from "../../../components/common/CollapsibleFilterBar";

const { Text } = Typography;

/**
 * 自动转发监控页面
 * 展示所有"自动转发规则"对入站邮件的处理决策（成功 / 跳过 / 失败）。
 *
 * 设计文档:
 *   docs/fzx_doc/MVT检测/HA-ZTO邮件自动转发/HA-ZTO邮件自动转发_功能规格说明书.md
 */
const AutoForwardList = () => {
    const { hasPermission, loading: authLoading, user } = useAuth();

    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    const { data, isLoading, refetch } = useLoadList(
        "autoForwardLogs",
        mailboxService.getAutoForwardList,
        {
            page: pagination.current,
            page_size: pagination.pageSize,
            ...filterParams,
        },
    );

    const {
        selectedRowKeys,
        clearSelection,
        rowSelection,
        getRowProps,
        getIndexColumn,
        tableRef,
    } = useTableSelection({
        rowKey: "log_id",
        dataSource: data?.items || [],
        pagination,
    });

    // 禁用整行点击选中（仅序号列）
    const getCustomRowProps = useCallback(
        (record, index) => {
            const originalProps = getRowProps(record, index);
            const { onClick, ...restProps } = originalProps;
            return restProps;
        },
        [getRowProps],
    );

    const handleTableChange = useCallback((newPagination) => {
        setPagination(newPagination);
    }, []);

    const handleReset = useCallback(() => {
        setFilterParams({});
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const filterSummaryItems = useMemo(() => {
        const items = [];
        if (filterParams.subject_keyword) {
            items.push({
                label: "主题",
                value:
                    filterParams.subject_keyword.length > 12
                        ? filterParams.subject_keyword.substring(0, 12) + "..."
                        : filterParams.subject_keyword,
                fullValue: filterParams.subject_keyword,
            });
        }
        if (filterParams.rule_category_id) {
            items.push({ label: "规则", value: filterParams.rule_category_id });
        }
        if (filterParams.decision) {
            const decisionMap = {
                sent: "已转发",
                skipped_duplicate: "已跳过(重复)",
                failed_no_recipients: "漏转(无收件人)",
                failed_other: "失败",
            };
            items.push({
                label: "决策",
                value: decisionMap[filterParams.decision] || filterParams.decision,
            });
        }
        if (filterParams.start_date || filterParams.end_date) {
            items.push({
                label: "日期",
                value: `${filterParams.start_date || "..."} ~ ${filterParams.end_date || "..."}`,
            });
        }
        return items;
    }, [filterParams]);

    const quickSearch = useQuickSearch({
        setFilterParams,
        setPagination,
        paramKey: "subject_keyword",
    });

    const handleQuickDecisionChange = useCallback((val) => {
        setFilterParams((prev) => {
            const next = { ...prev };
            if (val) {
                next.decision = val;
            } else {
                delete next.decision;
            }
            return next;
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    if (authLoading) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>正在加载用户信息...</p>
                    </div>
                </Card>
            </div>
        );
    }

    if (!hasPermission("mail:read")) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <h3>无权限访问</h3>
                        <p>您没有查看自动转发监控的权限，请联系管理员。</p>
                        <p style={{ color: "#666", fontSize: "12px", marginTop: 16 }}>
                            当前用户: {user?.username || "未知"} | 权限列表:{" "}
                            {user?.permissions?.length ? user.permissions.join(", ") : "无权限"}
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    // forward_status → 展示样式
    const getForwardStatusConfig = (status) => {
        const map = {
            success: { status: "success", text: "全部成功", color: "green" },
            partial_failed: { status: "warning", text: "部分失败", color: "orange" },
            failed: { status: "error", text: "失败", color: "red" },
            skipped: { status: "default", text: "已跳过(重复)", color: "default" },
            pending: { status: "processing", text: "发送中", color: "blue" },
        };
        return map[status] || { status: "default", text: status || "未知", color: "default" };
    };

    const getDecisionTag = (decision) => {
        const map = {
            sent: { color: "green", text: "已转发" },
            skipped_duplicate: { color: "default", text: "跳过(重复)" },
            failed_no_recipients: { color: "red", text: "漏转(无收件人)" },
            failed_other: { color: "red", text: "失败" },
        };
        const cfg = map[decision] || { color: "default", text: decision };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
    };

    const getRecipientStatusConfig = (status) => {
        const map = {
            SENT: { color: "green", text: "已发送" },
            FAILED: { color: "red", text: "失败" },
            PENDING: { color: "blue", text: "待发送" },
            SENDING: { color: "blue", text: "发送中" },
            SKIPPED: { color: "default", text: "已跳过" },
        };
        return map[status] || { color: "default", text: status || "未知" };
    };

    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "原邮件主题",
            dataIndex: "raw_email_subject",
            key: "raw_email_subject",
            ellipsis: true,
            width: 280,
            render: (text) => (
                <Tooltip title={text}>
                    <span>{text || "(无主题)"}</span>
                </Tooltip>
            ),
        },
        {
            title: "发件人",
            dataIndex: "raw_email_from",
            key: "raw_email_from",
            ellipsis: true,
            width: 170,
            render: (text) => (
                <Tooltip title={text}>
                    <span>{text || "-"}</span>
                </Tooltip>
            ),
        },
        {
            title: "抓取时间",
            dataIndex: "raw_email_received_at",
            key: "raw_email_received_at",
            width: 150,
            render: (text) => (text ? formatDateTime(text) : "-"),
        },
        {
            title: (
                <span>
                    规则{" "}
                    <Tooltip title="触发本次决策的自动转发规则，对应 backend/run_mail.py 配置中以 forward_ 开头的分类">
                        <InfoCircleOutlined style={{ color: "#999", fontSize: 12, cursor: "help" }} />
                    </Tooltip>
                </span>
            ),
            dataIndex: "rule_name",
            key: "rule_name",
            width: 210,
            ellipsis: true,
            render: (text, record) => (
                <Tooltip title={text ? `${text} (${record.rule_id})` : record.rule_id}>
                    <Tag
                        color="purple"
                        icon={<SwapOutlined />}
                        style={{
                            maxWidth: "100%",
                            display: "inline-flex",
                            alignItems: "center",
                            overflow: "hidden",
                        }}
                    >
                        <span
                            style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {text || record.rule_id}
                        </span>
                    </Tag>
                </Tooltip>
            ),
        },
        {
            title: "状态",
            dataIndex: "forward_status",
            key: "forward_status",
            width: 110,
            render: (status, record) => {
                const cfg = getForwardStatusConfig(status);
                const tooltip = record.reason
                    ? record.reason
                    : record.decision
                    ? `决策: ${record.decision}`
                    : null;
                return (
                    <Tooltip title={tooltip}>
                        <Badge status={cfg.status} text={cfg.text} />
                    </Tooltip>
                );
            },
        },
        {
            title: "决策",
            dataIndex: "decision",
            key: "decision",
            width: 130,
            render: (decision) => getDecisionTag(decision),
        },
        {
            title: "收件人",
            dataIndex: "recipients",
            key: "recipients",
            width: 200,
            ellipsis: true,
            render: (recipients, record) => {
                const list = recipients || [];
                if (list.length === 0) {
                    if (record.recipient_group_name) {
                        return (
                            <Tooltip title={`配置的收件人分组: ${record.recipient_group_name}`}>
                                <Text type="secondary">
                                    <UserOutlined /> -
                                </Text>
                            </Tooltip>
                        );
                    }
                    return <Text type="secondary">-</Text>;
                }
                const allEmails = list.map((r) => r.email).join(", ");
                const first = list[0];
                const moreCount = list.length - 1;
                return (
                    <Tooltip title={allEmails}>
                        <span>
                            <UserOutlined style={{ color: "#999", marginRight: 4 }} />
                            {first.email}
                            {moreCount > 0 && <Text type="secondary"> (+{moreCount})</Text>}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "决策时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 150,
            render: (text) => (text ? formatDateTime(text) : "-"),
        },
    ];

    const expandedRowRender = (record) => {
        const recipients = record.recipients || [];
        return (
            <Card size="small" bordered={false} style={{ background: "#fafafa", marginInline: 8 }}>
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <div>
                        <Text strong>规则:</Text>{" "}
                        <Tag color="purple">{record.rule_name || record.rule_id}</Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            ({record.rule_id})
                        </Text>
                    </div>
                    <div>
                        <Text strong>分组:</Text>{" "}
                        {record.recipient_group_name || <Text type="secondary">(未指定)</Text>}
                    </div>
                    {record.reason && (
                        <div>
                            <Text strong>原因:</Text>{" "}
                            <Text type="secondary">{record.reason}</Text>
                        </div>
                    )}
                    <div>
                        <Text strong>转发邮件 ID:</Text>{" "}
                        {record.forwarded_raw_email_id ? (
                            <Tag color="blue">#{record.forwarded_raw_email_id}</Tag>
                        ) : (
                            <Text type="secondary">未生成（跳过 / 漏转 / 失败）</Text>
                        )}
                        {record.forwarded_at && (
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                发送于 {formatDateTime(record.forwarded_at)}
                            </Text>
                        )}
                    </div>
                    {recipients.length > 0 && (
                        <div>
                            <Text strong style={{ display: "block", marginBottom: 4 }}>
                                收件人详情:
                            </Text>
                            <Table
                                size="small"
                                pagination={false}
                                rowKey={(r, idx) => `${record.log_id}-${r.email}-${idx}`}
                                dataSource={recipients}
                                columns={[
                                    {
                                        title: "邮箱",
                                        dataIndex: "email",
                                        key: "email",
                                        width: "30%",
                                    },
                                    {
                                        title: "类型",
                                        dataIndex: "type",
                                        key: "type",
                                        width: 70,
                                        render: (t) => <Tag>{t}</Tag>,
                                    },
                                    {
                                        title: "状态",
                                        dataIndex: "status",
                                        key: "status",
                                        width: 90,
                                        render: (s) => {
                                            const cfg = getRecipientStatusConfig(s);
                                            return <Tag color={cfg.color}>{cfg.text}</Tag>;
                                        },
                                    },
                                    {
                                        title: "发送时间",
                                        dataIndex: "sent_at",
                                        key: "sent_at",
                                        width: 160,
                                        render: (t) => (t ? formatDateTime(t) : "-"),
                                    },
                                    {
                                        title: "错误信息",
                                        dataIndex: "error_message",
                                        key: "error_message",
                                        ellipsis: true,
                                        render: (msg) =>
                                            msg ? (
                                                <Tooltip title={msg}>
                                                    <Text type="danger">{msg}</Text>
                                                </Tooltip>
                                            ) : (
                                                <Text type="secondary">-</Text>
                                            ),
                                    },
                                ]}
                            />
                        </div>
                    )}
                </Space>
            </Card>
        );
    };

    return (
        <div className="page-container">
            <div
                className="page-header-compact"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <div className="page-title-compact">
                    <SwapOutlined style={{ marginRight: 8 }} />
                    自动转发监控
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={refetch} loading={isLoading}>
                        刷新
                    </Button>
                </Space>
            </div>

            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleReset}
                drawerTitle="自动转发监控 - 筛选条件"
                quickControls={
                    <Space size={8} align="center">
                        <Input
                            placeholder="搜索原邮件主题..."
                            style={{ width: 220, height: 26 }}
                            {...quickSearch.inputProps}
                        />
                        <Select
                            placeholder="决策"
                            size="small"
                            style={{ width: 160, height: 26 }}
                            allowClear
                            value={filterParams.decision || undefined}
                            onChange={handleQuickDecisionChange}
                            options={[
                                { value: "sent", label: "已转发" },
                                { value: "skipped_duplicate", label: "跳过(重复)" },
                                { value: "failed_no_recipients", label: "漏转(无收件人)" },
                                { value: "failed_other", label: "失败" },
                            ]}
                        />
                    </Space>
                }
            >
                <div style={{ color: "#999", fontSize: 12 }}>
                    本页展示所有"自动转发规则"对入站邮件的处理决策。规则配置详见
                    <code style={{ marginInline: 4 }}>backend/run_mail.py</code>
                    中以 <code>forward_</code> 开头的分类；收件人来自"邮箱地址与分组"页面所配置的分组。
                </div>
            </CollapsibleFilterBar>

            {selectedRowKeys.length > 0 && (
                <div className="batch-operations-info">
                    <span style={{ marginRight: 8, fontSize: "12px" }}>
                        已选择{" "}
                        <span style={{ fontWeight: "bold" }}>{selectedRowKeys.length}</span> 条
                    </span>
                    <Divider type="vertical" style={{ margin: "0 4px" }} />
                    <Button
                        size="small"
                        type="link"
                        onClick={clearSelection}
                        style={{ padding: "0 4px" }}
                    >
                        取消选择
                    </Button>
                </div>
            )}

            <Card className="data-card">
                <Table
                    size="small"
                    columns={columns}
                    dataSource={data?.items || []}
                    rowKey="log_id"
                    pagination={{
                        ...pagination,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条决策记录`,
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1520 }}
                    rowSelection={{
                        ...rowSelection,
                        columnWidth: 6,
                    }}
                    ref={tableRef}
                    onRow={getCustomRowProps}
                    expandable={{
                        expandedRowKeys,
                        onExpandedRowsChange: setExpandedRowKeys,
                        expandedRowRender,
                        rowExpandable: () => true,
                    }}
                />
            </Card>
        </div>
    );
};

export default AutoForwardList;
